import CategoryGroupIcon from '@/components/cashflow/CategoryGroupIcon';
import CategoryIcon from '@/components/cashflow/CategoryIcon';
import { buildCategoryGroups } from '@/components/cashflow/helpers';
import MerchantLogo from '@/components/cashflow/MerchantLogo';
import { AnyFinancialEntity } from '@common/financialEntities';
import { Goal } from '@common/goals';
import { TransactionEntryCategoryFieldsFragment } from '@common/graphql/generated';
import {
    BaseSpendingGroup,
    buildBaseSpendingGroups,
    CashflowTransactionEntry,
    SpendingGroupBy,
} from '@common/react/cashflow';
import { getUpcomingGoalTransactions } from '@common/react/goals';
import { DateRange } from '@common/types';
import { sortBy } from '@common/util/arrays';
import { dt } from '@common/util/dateTime';
import { app } from '@common/zapatos';

type UpcomingTransaction = {
    id: string;
    name: string;
    label: string;
    amount: number;
    date: string;
    key: string;
    goal: Goal;
};

export type SpendingGroup = BaseSpendingGroup & {
    icon: JSX.Element;
};

export const buildSpendingGroups = ({
    transactionEntries,
    groupBy,
}: {
    transactionEntries: CashflowTransactionEntry[];
    groupBy: SpendingGroupBy;
}): SpendingGroup[] => {
    const baseSpendingGroups = buildBaseSpendingGroups({
        transactionEntries,
        groupBy,
    });

    return baseSpendingGroups.map((spendingGroup) => ({
        ...spendingGroup,
        icon:
            groupBy === 'category'
                ? CategoryIcon({ category: spendingGroup.category })
                : groupBy === 'categoryGroup'
                  ? CategoryGroupIcon({
                        categoryGroup: spendingGroup.category.categoryGroup,
                    })
                  : MerchantLogo({ merchant: spendingGroup.merchant }),
    }));
};

export const getUpcomingSavingInvestingTransactions = ({
    goals,
    dateRange,
    userName,
    partnerName,
    financialEntities,
}: {
    goals: Goal[];
    dateRange: DateRange;
    userName?: string;
    partnerName?: string | null;
    financialEntities: AnyFinancialEntity[];
}): UpcomingTransaction[] => {
    const upcomingTransactions: UpcomingTransaction[] = [];

    for (const goal of goals) {
        for (const t of getUpcomingGoalTransactions({
            goal,
            financialEntities,
        })) {
            if (t.date < dateRange.startDate || t.date > dateRange.endDate) {
                continue;
            }

            const transactionOwnerName = t.isMine
                ? userName || 'Mine'
                : partnerName || 'Partner';

            upcomingTransactions.push({
                id: goal.id,
                key: [t.id, t.type, t.amount, t.date].join('-'),
                name: t.name,
                label: `${transactionOwnerName}, ${dt(t.date).format('MMM DD')}`,
                amount: t.amount,
                date: t.date,
                goal,
            });
        }
    }

    return sortBy(upcomingTransactions, 'date', 'ASC');
};

export const getCategoryIds = (
    categories: TransactionEntryCategoryFieldsFragment[],
    supercategory: app.supercategory
) => {
    const groups = buildCategoryGroups(categories).supercategoryGroups;
    const categoryGroup = groups[supercategory];
    return categoryGroup ? categoryGroup.options.map((o) => o.value) : [];
};
